import React, { useEffect, useState } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import "./BookingPaymentPage.css";
import { getCoinBalance } from "../services/coinService";
import NavUser from "../components/NavUser";
import { 
  FaCreditCard, 
  FaUniversity, 
  FaMoneyBillWave, 
  FaShieldAlt, 
  FaLock, 
  FaCheckCircle,
  FaCalendarAlt,
  FaUser,
  FaInfoCircle,
  FaChevronDown,
  FaHotel,
  FaMapMarkedAlt
} from "react-icons/fa";
import { getApiBaseUrl } from "../utils/apiConfig";
import PaymentService from "../services/PaymentService";

function BookingPaymentPage() {
  const { type, bookingId } = useParams();
  const [booking, setBooking] = useState(null);
  const [message, setMessage] = useState("");
  const [coinBalance, setCoinBalance] = useState(0);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState("payments_lk");
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [isDetailsExpanded, setIsDetailsExpanded] = useState(true);
  
  const API_URL = getApiBaseUrl();

  useEffect(() => {
    const fetchBooking = async () => {
      setLoading(true);
      try {
        const endpoint = type === "tour" ? "tour-bookings" : "bookings";
        const response = await axios.get(`${API_URL}/${endpoint}/${bookingId}`);
        const data = response.data.booking || response.data;
        setBooking(data);
      } catch (error) {
        console.error("Failed to fetch booking:", error);
        setMessage("Failed to load booking details.");
      } finally {
        setLoading(false);
      }
    };

    const fetchCoinBalance = async () => {
      const userData = localStorage.getItem("user");
      if (userData) {
        const user = JSON.parse(userData);
        const balance = await getCoinBalance(user.id);
        setCoinBalance(balance);
      }
    };

    fetchBooking();
    fetchCoinBalance();
  }, [bookingId, type, API_URL]);


  const handleKokoPayment = async () => {
    if (!booking) return;
    setProcessing(true);
    try {
      const endpoint = type === "tour" ? "/payment/koko/tour/start" : "/payment/koko/start";
      const payload = type === "tour" ? {
          userId: booking.user_id,
          tourListingId: booking.tour_listing_id,
          price: booking.price,
          bookingDate: booking.booking_date,
          numberOfGuests: booking.number_of_guests,
          pickupRequested: booking.pickup_requested,
          specialRequests: booking.special_requests,
          isAdvance: false,
          coins_used_amount: booking.coins_used_amount || 0,
      } : {
          userId: booking.user_id,
          listingId: booking.listing_id,
          price: booking.price,
          checkingDate: booking.checking_date,
          checkoutDate: booking.checkout_date,
          numberOfGuests: booking.number_of_guests,
          pickupRequested: booking.pickup_requested,
          specialRequests: booking.special_requests,
          isAdvance: false,
          coins_used_amount: booking.use_coin_balance || 0,
      };

      const response = await axios.post(`${API_URL}${endpoint}`, payload);
      const { actionUrl, fields } = response.data || {};

      if (!actionUrl || !fields || Object.keys(fields).length === 0) {
        alert("Koko payment service is temporarily unavailable. Please try another payment method.");
        setProcessing(false);
        return;
      }

      const form = document.createElement("form");
      form.method = "POST";
      form.action = actionUrl;

      for (const key in fields) {
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = key;
        input.value = fields[key];
        form.appendChild(input);
      }

      document.body.appendChild(form);
      form.submit();
    } catch (err) {
      console.error("KOKO Error:", err);
      setProcessing(false);
    }
  };

  const handlePaymentsLkPayment = async () => {
    if (!booking) return;
    setProcessing(true);
    setMessage("");

    try {
      const result = await PaymentService.createPaymentsLkCheckout({
        bookingType: type || "hotel",
        bookingId: booking.id,
        clientType: "web",
      });

      if (result?.checkoutUrl) {
        window.location.href = result.checkoutUrl;
      } else {
        setMessage(result?.error || "Failed to retrieve Payments.lk checkout URL.");
        setProcessing(false);
      }
    } catch (err) {
      console.error("Payments.lk Checkout Initiation Error:", err);
      const errMsg =
        err.response?.data?.error ||
        err.response?.data?.message ||
        "Failed to initiate Payments.lk secure checkout.";
      setMessage(errMsg);
      setProcessing(false);
    }
  };

  const handlePayment = () => {
    switch (selectedPaymentMethod) {
      case "payments_lk":
        handlePaymentsLkPayment();
        break;
      case "koko":
        handleKokoPayment();
        break;
      default:
        alert("Please select a payment method");
    }
  };

  if (loading) {
    return (
      <div className="loading-state">
        <NavUser />
        <div className="loading-spinner"></div>
        <p className="loading-text">Preparing your secure payment environment...</p>
      </div>
    );
  }

  return (
    <div className="payment-container">
      <NavUser />
      
      <h2>{type === "tour" ? "Guide Booking Payment" : "Hotel Booking Payment"}</h2>
      
      {message && (
        <div className="payment-message error">
          <FaInfoCircle /> {message}
        </div>
      )}

      {booking && (
        <>
          <div className="booking-details-card">
            <div className="expandable-header active" onClick={() => setIsDetailsExpanded(!isDetailsExpanded)}>
              <h3>
                {type === "tour" ? <FaMapMarkedAlt /> : <FaHotel />} 
                {type === "tour" ? booking.tour_listing?.title : booking.listing?.name}
              </h3>
              <FaChevronDown className={`expandable-icon ${isDetailsExpanded ? 'active' : ''}`} />
            </div>
            
            <div className={`expandable-content ${isDetailsExpanded ? 'expanded' : ''}`}>
               <p><FaInfoCircle /> {type === "tour" ? booking.tour_listing?.description : booking.listing?.description}</p>
               
               <div className="price-highlight">
                  Rs. {parseFloat(booking.price).toLocaleString()}
               </div>

               <h4>Booking Details</h4>
               <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                  <p><strong><FaLock /> Order ID:</strong> #{booking.id}</p>
                  <p><strong><FaUser /> Guest Name:</strong> {booking.user?.full_name}</p>
                  <p><strong><FaUser /> Guests:</strong> {booking.number_of_guests}</p>
                  <p><strong><FaCalendarAlt /> Booking Date:</strong> {new Date(booking.booking_date || booking.checking_date).toLocaleDateString()}</p>
                  {type === "hotel" && (
                    <>
                      <p><strong><FaCalendarAlt /> Check-in:</strong> {new Date(booking.checking_date).toLocaleDateString()}</p>
                      <p><strong><FaCalendarAlt /> Check-out:</strong> {new Date(booking.checkout_date).toLocaleDateString()}</p>
                    </>
                  )}
                  <p><strong><FaCheckCircle /> Status:</strong> <span className="status-badge pending">{booking.booking_status?.name || "Pending"}</span></p>
               </div>

               <hr />
               <h4>Loyalty Rewards</h4>
               <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f0f7ff', padding: '12px', borderRadius: '8px' }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><FaMoneyBillWave color="#1a73e8" /> Available Coins:</span>
                  <span style={{ fontWeight: '700', color: '#1a73e8' }}>🪙 {coinBalance} coins</span>
               </div>
            </div>
          </div>

          <div className="payment-method-selection">
            <h3><FaCreditCard /> Select Payment Method</h3>
            <div className="payment-method-cards">
              <div 
                className={`payment-method-card ${selectedPaymentMethod === "payments_lk" ? "selected" : ""}`}
                onClick={() => setSelectedPaymentMethod("payments_lk")}
              >
                <FaCreditCard className="payment-method-icon" />
                <div className="payment-method-name">
                  Payments.lk
                  <span style={{ fontSize: '11px', background: '#e0f2fe', color: '#0284c7', padding: '2px 6px', borderRadius: '4px', marginLeft: '6px', fontWeight: 'bold' }}>
                    Recommended
                  </span>
                </div>
                <div className="payment-method-description">Visa, Mastercard, AMEX, LankaPay, QR</div>
              </div>

              <div 
                className={`payment-method-card ${selectedPaymentMethod === "koko" ? "selected" : ""}`}
                onClick={() => setSelectedPaymentMethod("koko")}
              >
                <FaUniversity className="payment-method-icon" />
                <div className="payment-method-name">KOKO</div>
                <div className="payment-method-description">Pay in 3 Interest-Free Installments</div>
              </div>
            </div>
          </div>

          <div className="security-indicators">
            <div className="security-indicator">
              <FaLock className="security-icon" /> 256-bit SSL Secure
            </div>
            <div className="security-indicator">
              <FaShieldAlt className="security-icon" /> PCI DSS Compliant
            </div>
            <div className="security-indicator">
              <FaShieldAlt className="security-icon" style={{ color: '#10b981' }} /> SafeStay Guarantee
            </div>
          </div>

          <div style={{
            background: '#f8fafc',
            border: '1px solid #e2e8f0',
            borderRadius: '10px',
            padding: '16px',
            marginTop: '15px',
            marginBottom: '20px',
            fontSize: '13px',
            color: '#4a5568',
            lineHeight: '1.6'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: '700', color: '#2d3748', marginBottom: '8px' }}>
              <FaInfoCircle color="#3182ce" /> Payment &amp; Cancellation Governance (v2026.09.01 | Draft: v2.0.0)
            </div>
            <p style={{ margin: '0 0 6px 0' }}>
              <strong>Merchant:</strong> SOLOWAY.LK (PVT) LTD (Company Reg: PV 00373363) (Order: <code>#{booking.id}</code>).
            </p>
            <p style={{ margin: '0 0 6px 0' }}>
              Card checkouts are processed externally via <strong>Payments.lk</strong> (brand operated by Payable (Pvt) Ltd). SoloWay does not store card numbers or CVVs.
            </p>
            <p style={{ margin: '0 0 6px 0' }}>
              <FaLock style={{ marginRight: '4px', verticalAlign: 'middle' }} />
              <strong>Confirmation Notice:</strong> Bookings remain in pending verification until an authoritative server confirmation is received. Returning from checkout does not guarantee completion. If funds are deducted but your booking remains pending, contact SoloWay support with your booking reference. Never provide card numbers or passwords to support.
            </p>
            <div style={{ marginTop: '6px', fontWeight: '600' }}>Standard Cancellation Policy:</div>
            <ul style={{ margin: '4px 0 8px 18px', padding: 0, lineHeight: '1.6' }}>
              <li><strong>&gt; 48 hours prior to arrival/start:</strong> 100% refund eligibility.</li>
              <li><strong>24–48 hours prior to arrival/start:</strong> 50% partial refund eligibility.</li>
              <li><strong>&lt; 24 hours or No-Show:</strong> Non-refundable under standard policy.</li>
            </ul>
            <div style={{ fontSize: '12px', color: '#718096' }}>
              Actual settlement timing depends on cardholder issuing bank clearing. By proceeding, you agree to SoloWay's <a href="/policies?tab=terms" target="_blank" rel="noopener noreferrer" style={{ color: '#3182ce', textDecoration: 'underline' }}>Terms &amp; Conditions</a> and <a href="/policies?tab=refunds" target="_blank" rel="noopener noreferrer" style={{ color: '#3182ce', textDecoration: 'underline' }}>Refund Policy</a>.
            </div>
          </div>

          <button 
            className={`payment-button ${processing ? 'loading' : ''}`} 
            onClick={handlePayment}
            disabled={processing}
          >
            {processing ? "Processing..." : `Pay Rs. ${parseFloat(booking.price).toLocaleString()}`}
          </button>
        </>
      )}
    </div>
  );
}

export default BookingPaymentPage;
